/**
 * Constants for product tours
 */

/** Configuration for a product tour: storage key, trigger paths, and version for change detection */
export interface TourConfig {
  storageKey: string;
  triggerPaths: string[];  // Paths where this tour should trigger (without language prefix)
  version: number;
}

/**
 * What a pre-versioning `"true"` in localStorage counts as.
 *
 * The key used to hold a boolean, so a browser that saw the tour before this release says only
 * "seen", not "seen at which version". Version 1 is the honest floor: it never over-claims, so a
 * user is at worst shown a step they have already had, never silently denied a new one. In the main
 * app the backend's `onboardingTourVersion` is the sharper of the two sources and wins anyway
 * (`getSeenVersion` takes the higher); this only decides on its own for the admin tour, which the
 * backend does not track and which is still on version 1.
 */
export const LEGACY_SEEN_VERSION = 1;

/** Centralized tour name identifiers to avoid string literals */
export const TOUR_NAMES = {
  onboardingTour: 'onboardingTour',
  adminOnboardingTour: 'adminOnboardingTour',
} as const;

/** Valid tour name keys derived from the TOUR_NAMES constant */
export type TourName = keyof typeof TOUR_NAMES;

/** Configuration map for each product tour, keyed by tour name */
export const TOUR_CONFIG: Record<TourName, TourConfig> = {
  [TOUR_NAMES.onboardingTour]: {
    storageKey: 'onboarding-tour-seen',
    triggerPaths: ['/'],  // Only triggers on home/today page
    // 3 (IPD-2981): the tour opens on the Today tab. Everyone below this version is shown the whole
    // tour again, not just the step that changed.
    version: 3,
  },
  [TOUR_NAMES.adminOnboardingTour]: {
    storageKey: 'admin-onboarding-tour-seen',
    triggerPaths: ['/'],  // Only triggers on admin overview page
    version: 1,
  },
};

/** Removes all Driver.js popover/overlay DOM elements and resets active-element attributes */
export const cleanupDriverDOM = (): void => {
  document.querySelectorAll('.driver-popover').forEach(el => { el.remove(); });
  document.querySelectorAll('.driver-overlay').forEach(el => { el.remove(); });
  document.querySelectorAll('.driver-active-element').forEach(el => {
    el.classList.remove('driver-active-element');
    el.removeAttribute('aria-haspopup');
    el.removeAttribute('aria-expanded');
    el.removeAttribute('aria-controls');
  });
};
